import {ACCEPT_LANGUAGE} from '@on-the-go-web/sdk';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {RouterModule, Router} from '@angular/router';

import {distinctUntilChanged, filter, skip, Subject, takeUntil} from 'rxjs';
import {ThemeService} from './helpers/services/theme.service';
import {AuthService} from './helpers/services/auth.service';
import {
  EventTrackService,
  IconService,
  localeToLanguageCode,
  UserDataService,
} from '@on-the-go-web/library/helpers';
import {resolveBrowserLanguage} from './helpers/functions/language.functions';
import {TranslateService} from '@ngx-translate/core';

import {enUS} from 'date-fns/locale';
import {DateFnsConfigurationService} from 'ngx-date-fns';
import {locales} from '@on-the-go-web/library';

@Component({
  imports: [RouterModule],
  selector: 'otg-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private _themeService = inject(ThemeService);
  private _iconService = inject(IconService);
  private _authService = inject(AuthService);
  private _router = inject(Router);
  private _translate = inject(TranslateService);
  private _acceptLanguage = inject(ACCEPT_LANGUAGE);
  private _dateFnsConfig = inject(DateFnsConfigurationService);
  private _userData = inject(UserDataService);
  // Field initialiser, not ngOnInit: inject() needs an injection context, and
  // throwing here used to abort the rest of ngOnInit.
  private _eventTrack = inject(EventTrackService); // keep this for page views tracking

  private _destroy$ = new Subject<void>();

  constructor() {
    this._authService.loggedIn$
      .pipe(
        skip(1),
        distinctUntilChanged(),
        filter((loggedIn) => !loggedIn),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        // The add-business stepper (also served at /claim/:id) logs in/out inline via
        // "switch account", so stay there instead of bouncing to the login page.
        const url = this._router.url;
        if (!url.startsWith('/add-business') && !url.startsWith('/claim/')) {
          this._router.navigate(['/']);
        }
      });

    this._themeService.loadTheme();
    this._iconService.registerFlagIcons();
    this._iconService.registerCustomIcons();
  }

  ngOnInit() {
    // The initial language is already resolved from the browser locale by
    // provideTranslateService's `lang` config (see app.config.ts), so this
    // only needs to react to an explicit stored user locale - or its absence,
    // e.g. after switching to an account with no locale set, which must reset
    // back to the browser default rather than keep the previous account's lang.
    this._userData.user$.pipe(takeUntil(this._destroy$)).subscribe((user) => {
      if (user?.locale) {
        const targetLang = localeToLanguageCode(user.locale).toLowerCase();
        // Tell the SDK too. Category names come back from the API rendered, so
        // without this a user who picked a language differing from their
        // browser's reads a translated UI with untranslated category names.
        // Only a stored locale is pushed: with no explicit choice the browser's
        // own Accept-Language is the better signal and is left alone.
        this._acceptLanguage.next(targetLang);
        if (targetLang !== this._translate.getCurrentLang()) {
          this._translate.use(targetLang);
        }
      } else {
        const browserLang = resolveBrowserLanguage();
        if (browserLang !== this._translate.getCurrentLang()) {
          this._translate.use(browserLang);
        }
      }
    });

    this._translate.onLangChange.pipe(takeUntil(this._destroy$)).subscribe((event) => {
      this.updateDateFnsLocale(event.lang);
    });

    this.updateDateFnsLocale(this._translate.getCurrentLang() || 'en');
  }

  private updateDateFnsLocale(lang: string) {
    const locale = locales[lang] || enUS;

    this._dateFnsConfig.setLocale(locale);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
